import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@headlessui/react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  FaCreditCard, FaSignOutAlt, FaUser, FaCreditCard as FaPayment, FaFolder, FaMobile, FaGlobe, 
  FaDesktop, FaCog, FaBars, FaTimes, FaChevronDown, FaBell, FaSearch, FaChevronRight,
  FaHeadset, FaTicketAlt, FaCog as FaSettings, FaHome, FaClock, 
  FaCheckCircle, FaExclamationCircle, FaApple, FaAndroid, FaEdit, FaTrash, FaEye, FaLink,
  FaAd, FaGlobeAmericas, FaFileAlt
} from 'react-icons/fa';
import PaymentModal from './PaymentModal';
import UpgradeModal from './UpgradeModal';
import ProjectForm from './ProjectForm';
import ProjectsSection from './ProjectsSection';
import DocumentsSection from './DocumentsSection';
import SupportModulesSection from './SupportModulesSection';
import SupportTicketsSection from './SupportTicketsSection';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import DashboardOverview from './DashboardOverview';
import ProductFormModal from './ProductFormModal';
import Settings from './Settings';
import ShortLinks from '../pages/ShortLinks';
import InternalAds from '../pages/InternalAds';
import InternalAdsDetail from '../pages/InternalAdsDetail';
import FeedbackSupportModal from './FeedbackSupportModal';
import { 
  getToken, 
  getUser, 
  isTokenValid, 
  clearAuthData, 
  redirectToLogin, 
  authenticatedFetch,
  isTokenExpiringSoon,
  getTokenExpiry,
  getBaseUrl,
  getUserLimits
} from '../utils/tokenUtils';

const UserDashboard = ({ user: initialUser, onLogout, onGenerateDocument, supportTickets = [], onSupportTicketsRefresh }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(initialUser);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [supportModules, setSupportModules] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDocumentForPayment, setSelectedDocumentForPayment] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalSection, setUpgradeModalSection] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);



  // Handle upgrade click
  const handleUpgradeClick = (section = null) => {
    // Get cached user limits for plan check
    const limits = getUserLimits();
    const userPlan = limits?.plan || 'free';
    
    // Enterprise plan kullanıcıları için modal açma
    if (userPlan === 'enterprise') {
      return;
    }
    setUpgradeModalSection(section);
    setShowUpgradeModal(true);
  };

  // Handle Stripe payment
  const handleStripePayment = async () => {
    if (!selectedDocumentForPayment) return;
    
    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch('/api/payment/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: selectedDocumentForPayment.price || 0,
          productName: selectedDocumentForPayment.name || 'Document Generation',
          currency: 'usd',
          documentId: selectedDocumentForPayment.publicationId,
          userEmail: user?.email,
          successUrl: `${window.location.origin}/result/${selectedDocumentForPayment.publicationId}?payment=success&source=generator`,
          cancelUrl: `${window.location.origin}/dashboard?payment=cancelled`
        })
      });

      const result = await response.json();

      if (result.success && result.url) {
        // Redirect to Stripe checkout
        window.location.href = result.url;
      } else {
        throw new Error(result.message || 'Failed to initialize payment');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      alert('Payment initialization failed: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle skip payment (for development)
  const handleSkipPayment = async () => {
    if (!selectedDocumentForPayment) return;
    
    setIsSubmitting(true);
    try {
      // Update document status to completed
      const response = await authenticatedFetch(`/api/dashboard/documents/${selectedDocumentForPayment._id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        setShowPaymentModal(false);
        setSelectedDocumentForPayment(null);
        fetchUserData();
        showToast('Payment completed successfully!', 'success');
      } else {
        throw new Error('Failed to complete payment');
      }
    } catch (error) {
      console.error('Skip payment error:', error);
      alert('Payment completion failed: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current menu item from URL
  const getCurrentMenuItem = () => {
    const currentPath = location.pathname;
    if (currentPath === '/dashboard') return 'dashboard';
    if (currentPath === '/dashboard/projects') return 'projects';
    if (currentPath === '/dashboard/documents') return 'documents';
    if (currentPath === '/dashboard/shortlinks') return 'shortlinks';
    if (currentPath === '/dashboard/internal-ads') return 'internal-ads';
    if (currentPath.startsWith('/dashboard/internal-ads/')) return 'internal-ads-detail';
    if (currentPath === '/dashboard/support') return 'support';
    if (currentPath === '/dashboard/support/tickets') return 'tickets';
    if (currentPath === '/dashboard/settings') return 'settings';
    return 'dashboard';
  };


  // Check for payment success/failure in URL parameters (only once on mount)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get('payment');
    const subscriptionStatus = urlParams.get('subscription');
    const source = urlParams.get('source');

    // Only process if there are actual parameters
    if (paymentStatus || subscriptionStatus) {
      // Process payment status
      if (paymentStatus === 'success') {
        showToast('Payment successful! Your document is being generated.', 'success');
      } else if (paymentStatus === 'failed') {
        showToast('Payment failed. Please try again.', 'error');
      }

      // Process subscription status
      if (subscriptionStatus === 'success') {
        showToast('Subscription upgraded successfully!', 'success');
        // Refresh user data to get updated subscription
        fetchUserData();
      } else if (subscriptionStatus === 'failed') {
        showToast('Subscription upgrade failed. Please try again.', 'error');
      }

      // Clean up URL parameters immediately
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []); // Empty dependency array - only run once on mount
  
  // Project management states
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });


  useEffect(() => {
    fetchUserData();
    fetchDocuments();
  }, []);

  // Update user state when initialUser changes
  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  // Toast notification functions
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Handle feedback form submission
  const handleFeedbackSubmit = async (formData) => {
    try {
      const response = await authenticatedFetch('/api/support/proxy-support-tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supportModuleId: '68ec17c46ec8b36a0e5fc827',
          productId: '68ec1c1c6ec8b36a0e5fca2f',
          ...formData
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToast('Support ticket submitted successfully!', 'success');
        return result;
      } else {
        throw new Error(result.message || 'Failed to submit support ticket');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      showToast(error.message || 'An error occurred while submitting the support ticket', 'error');
      throw error;
    }
  };


  const fetchDocuments = async () => {
    try {
      const response = await authenticatedFetch('/api/dashboard/user/documents');
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        console.log('Documents fetched:', data.documents?.length || 0);
      } else {
        console.error('Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };



  const fetchUserData = async (retryCount = 0) => {
    try {
      // Check token validity before making any requests
      if (!isTokenValid()) {
        console.log('Token is invalid or expired, redirecting to login');
        redirectToLogin();
        return;
      }

      console.log('Fetching fresh user data from backend...');

      // Check if token is expiring soon and show warning
      if (isTokenExpiringSoon()) {
        console.warn('Token is expiring soon, consider refreshing');
      }

      console.log('Making API requests...');
      
      // Make API requests with individual error handling
      const apiRequests = [
        { name: 'user', url: '/api/auth/me' },
        { name: 'documents', url: '/api/dashboard/user/documents' },
        { name: 'payments', url: '/api/dashboard/user/payments' },
        { name: 'projects', url: '/api/dashboard/user/projects?limit=0' },
        { name: 'supportModules', url: '/api/support/user/support-modules' },
        { name: 'stats', url: '/api/dashboard/user/stats' },
        { name: 'internalAds', url: '/api/internal-ads/placements' }
      ];

      const results = {};
      const failedRequests = [];

      // Execute requests with individual error handling
      await Promise.allSettled(
        apiRequests.map(async (request) => {
          try {
            const response = await authenticatedFetch(request.url);
            results[request.name] = response;
            console.log(`${request.name} API request successful:`, response.status);
          } catch (error) {
            console.error(`${request.name} API request failed:`, error.message);
            failedRequests.push({ name: request.name, error: error.message });
            results[request.name] = null;
          }
        })
      );
      
      console.log('API responses received:', {
        user: results.user?.status,
        documents: results.documents?.status,
        payments: results.payments?.status,
        projects: results.projects?.status,
        supportModules: results.supportModules?.status,
        stats: results.stats?.status,
        internalAds: results.internalAds?.status
      });

      if (failedRequests.length > 0) {
        console.error('Some API requests failed:', failedRequests);
      }

      // Parse JSON responses with error handling
      const parseResponse = async (response, defaultValue = {}) => {
        if (!response) return defaultValue;
        try {
          return await response.json();
        } catch (error) {
          console.error('Error parsing JSON response:', error);
          return defaultValue;
        }
      };

      const [userData, documentsData, paymentsData, projectsData, supportModulesData, statsData, internalAdsData] = await Promise.all([
        parseResponse(results.user, {}),
        parseResponse(results.documents, { documents: [] }),
        parseResponse(results.payments, { payments: [] }),
        parseResponse(results.projects, { projects: [] }),
        parseResponse(results.supportModules, { supportModules: [] }),
        parseResponse(results.stats, {}),
        parseResponse(results.internalAds, { placements: [] })
      ]);

      console.log('Setting data:', {
        user: userData,
        documents: documentsData.documents?.length || 0,
        payments: paymentsData.payments?.length || 0,
        projects: projectsData.projects?.length || 0,
        supportModules: supportModulesData.supportModules?.length || 0,
        supportTickets: supportTickets?.length || 0,
        stats: statsData.stats,
        totalShortLinks: statsData.stats?.totalShortLinks
      });

      // Update user data with fresh subscription info
      if (userData && userData.user) {
        console.log('UserDashboard fetchUserData - API response:', userData);
        console.log('UserDashboard fetchUserData - User profile:', userData.user?.profile);
        
        // Update local storage with fresh user data
        localStorage.setItem('user', JSON.stringify(userData.user));
        // Update user state
        setUser(userData.user);
      }

      setDocuments(documentsData.documents || []);
      setPayments(paymentsData.payments || []);
      setProjects(projectsData.projects || []);
      setSupportModules(supportModulesData.supportModules || []);
      setStats({
        ...statsData.stats
      });

      // Fetch products for each support module's project
      const supportModulesWithProducts = await Promise.all(
        (supportModulesData.supportModules || []).map(async (module) => {
          if (module.projectId && module.projectId._id) {
            try {
              const productsRes = await authenticatedFetch(`/api/dashboard/user/projects/${module.projectId._id}/products`);
              const productsData = await productsRes.json();
              return {
                ...module,
                projectId: {
                  ...module.projectId,
                  products: productsData.products || []
                }
              };
            } catch (error) {
              console.error(`Error fetching products for project ${module.projectId._id}:`, error);
              return module;
            }
          }
          return module;
        })
      );
      setSupportModules(supportModulesWithProducts);

    } catch (error) {
      console.error('Error fetching user data:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Handle rate limiting with retry
      if (error.message.includes('Too many requests') && retryCount < 3) {
        console.log(`Rate limited, retrying in ${(retryCount + 1) * 2} seconds...`);
        setTimeout(() => {
          fetchUserData(retryCount + 1);
        }, (retryCount + 1) * 2000); // 2, 4, 6 seconds delay
        return;
      }
      
      // authenticatedFetch already handles 401 and redirects
      if (error.message === 'Authentication failed') {
        return; // Already redirected
      }
      
      // Show user-friendly error message
      if (error.message.includes('Too many requests')) {
        showToast('Server is busy. Please wait a moment and refresh the page.', 'error');
      } else {
        showToast('Failed to load data. Please refresh the page.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    onLogout();
  };

  const handleCreateProject = async (projectData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      const response = await authenticatedFetch('/api/dashboard/user/projects', {
        method: 'POST',
        body: projectData
      });

      const result = await response.json();
      setProjects(prev => [...prev, result.project]);
      setShowProjectForm(false);
      setEditingProject(null); // Reset editing state
      fetchUserData(); // Refresh stats
    } catch (error) {
      console.error('Create project error:', error);
      if (error.message !== 'Authentication failed') {
        // Check if it's a limit error
        if (error.message && error.message.includes('limitinize ulaştınız')) {
          handleUpgradeClick('projects');
        } else {
          showToast('Error occurred while creating project.', 'error');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProject = async (projectId, projectData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      console.log('UserDashboard - Updating project:', projectId);
      console.log('UserDashboard - Project data:', projectData);
      console.log('UserDashboard - Project data logo:', projectData.logo);
      
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}`, {
        method: 'PUT',
        body: projectData
      });

      console.log('UserDashboard - Update response status:', response.status);
      const result = await response.json();
      console.log('UserDashboard - Update result:', result);
      console.log('UserDashboard - Updated project logo:', result.project?.logo);
      
      setProjects(prev => prev.map(p => p._id === projectId ? result.project : p));
      setShowProjectForm(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Update project error:', error);
      if (error.message !== 'Authentication failed') {
        showToast('Error occurred while updating project.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}`, {
        method: 'DELETE'
      });

      setProjects(prev => prev.filter(p => p._id !== projectId));
      fetchUserData(); // Refresh stats
    } catch (error) {
      console.error('Delete project error:', error);
      if (error.message !== 'Authentication failed') {
        showToast('Error occurred while deleting project.', 'error');
      }
    }
  };

  const handleViewProject = (project) => {
    navigate(`/dashboard/projects/${project._id}`);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowProjectForm(true);
  };

  const handleAddProduct = (project) => {
    setSelectedProject(project);
    setShowProductForm(true);
  };

  const handleAddModule = (project) => {
    setSelectedProject(project);
    // TODO: Implement module form
    console.log('Add module for project:', project);
  };


  const handleUpgrade = async (paymentData) => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      const { authenticatedFetch } = await import('../utils/tokenUtils');
      
      const response = await authenticatedFetch('/api/dashboard/user/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData)
      });

      if (response.ok) {
        const result = await response.json();
        showToast('Subscription upgraded successfully!', 'success');
        
        // Refresh user data to get updated subscription info
        await fetchUserData();
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Subscription upgrade failed.', 'error');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      showToast('An error occurred during subscription upgrade.', 'error');
    }
  };


  const handleCreateProduct = async (productData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      console.log('Creating product with data:', productData);
      console.log('Selected project ID:', selectedProject._id);
      
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${selectedProject._id}/products`, {
        method: 'POST',
        body: productData
      });

      console.log('Product creation response:', response);
      const result = await response.json();
      console.log('Product creation result:', result);
      
      setProjects(prev => prev.map(p => 
        p._id === selectedProject._id 
          ? { ...p, products: [...(p.products || []), result.product] }
          : p
      ));
      setShowProductForm(false);
      setSelectedProject(null);
      fetchUserData(); // Refresh stats
      showToast('Product created successfully!', 'success');
    } catch (error) {
      console.error('Create product error:', error);
      console.error('Error details:', error.message);
      if (error.message !== 'Authentication failed') {
        showToast(`Error occurred while creating product: ${error.message}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <DashboardSidebar 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onShowLogoutDialog={setShowLogoutDialog}
        onShowFeedbackModal={setShowFeedbackModal}
        supportTickets={supportTickets}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <DashboardHeader 
          user={user}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onUpgradeClick={handleUpgradeClick}
          customTitle={getCurrentMenuItem() === 'internal-ads-detail' ? 'Internal Ad Analytics' : null}
          showBackButton={getCurrentMenuItem() === 'internal-ads-detail'}
          onBackClick={() => navigate('/dashboard/internal-ads')}
        />

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          {getCurrentMenuItem() === 'dashboard' && (
            <DashboardOverview />
          )}

          {getCurrentMenuItem() === 'projects' && (
            <div className="p-6">
              <ProjectsSection 
                user={user} 
                onLogout={onLogout} 
                onStatsUpdate={fetchUserData}
              />
            </div>
          )}

          {getCurrentMenuItem() === 'support' && (
            <div className="p-6">
              <SupportModulesSection 
                user={user}
                supportModules={supportModules}
                projects={projects}
                onStatsUpdate={fetchUserData}
                onShowToast={showToast}
              />
            </div>
          )}

          {getCurrentMenuItem() === 'documents' && (
            <div className="p-6">
              <DocumentsSection 
                user={user}
                documents={documents}
                projects={projects}
                onStatsUpdate={fetchUserData}
                onShowToast={showToast}
                onRefreshDocuments={fetchDocuments}
              />
                </div>
          )}

          {getCurrentMenuItem() === 'shortlinks' && (
            <div className="p-6">
              <ShortLinks user={user} onLogout={onLogout} onStatsUpdate={fetchUserData} />
            </div>
          )}

          {getCurrentMenuItem() === 'internal-ads' && (
            <div className="p-6">
              <InternalAds user={user} onLogout={onLogout} onStatsUpdate={fetchUserData} />
            </div>
          )}

          {getCurrentMenuItem() === 'internal-ads-detail' && (
            <InternalAdsDetail user={user} onLogout={onLogout} onStatsUpdate={fetchUserData} />
          )}

          {getCurrentMenuItem() === 'tickets' && (
            <div className="p-6">
              <SupportTicketsSection 
                supportTickets={supportTickets}
                projects={projects}
                supportModules={supportModules}
                onShowToast={showToast}
                onRefresh={onSupportTicketsRefresh || fetchUserData}
              />
            </div>
          )}


          {getCurrentMenuItem() === 'settings' && (
            <div className="p-6">
              <Settings user={user} onLogout={handleLogout} onUserUpdate={fetchUserData} onUserChange={setUser} onShowLogoutDialog={setShowLogoutDialog} />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ProjectForm
        isOpen={showProjectForm}
        onClose={() => {
          setShowProjectForm(false);
          // Don't reset editingProject here to prevent flash
        }}
        onSubmit={editingProject ? (data) => handleUpdateProject(editingProject._id, data) : handleCreateProject}
        project={editingProject}
        isLoading={isSubmitting}
      />

      <ProductFormModal
        isOpen={showProductForm}
        onClose={() => {
          setShowProductForm(false);
          setSelectedProject(null);
        }}
        onSubmit={handleCreateProduct}
        project={selectedProject}
        isLoading={isSubmitting}
      />

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPayWithStripe={handleStripePayment}
        onSkipPayment={handleSkipPayment}
        onCancel={() => setShowPaymentModal(false)}
        isLoading={isSubmitting}
        productInfo={selectedDocumentForPayment}
        userEmail={user?.email}
      />


      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
        onUpgrade={handleUpgrade}
        highlightSection={upgradeModalSection}
      />

      <FeedbackSupportModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        user={user}
      />



        {/* Toast Notification */}
        {toast.show && (
          <div className="fixed top-4 right-4 z-[60]">
            <div className={`px-4 py-3 rounded-lg shadow-lg border transition-all duration-300 ${
              toast.type === 'success' 
                ? 'bg-green-900 border-green-700 text-green-300' 
                : 'bg-red-900 border-red-700 text-red-300'
            }`}>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  toast.type === 'success' ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className="text-sm font-medium">{toast.message}</span>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Support Modal */}
        <FeedbackSupportModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
          user={user}
        />

        {/* Logout Confirmation Dialog */}
        <Transition appear show={showLogoutDialog} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowLogoutDialog(false)}>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-75" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white mb-4">
                      Log Out
                    </DialogTitle>
                    
                    <div className="mb-6">
                      <p className="text-sm text-gray-300">
                        Are you sure you want to log out? You will need to sign in again to access your account.
                      </p>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <Button
                        type="button"
                        onClick={() => setShowLogoutDialog(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors duration-200"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowLogoutDialog(false);
                          handleLogout();
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors duration-200"
                      >
                        Log Out
                      </Button>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>

      </div>
    );
  };

  export default UserDashboard;
